// Thin table renderer that takes a column catalog + rows and projects
// the user's visible-column selection. Each column defines its own
// renderer so reports don't have to repeat formatting logic in JSX.
//
// Props:
//   title       — string (shown above table)
//   subtitle    — optional string
//   reportId    — string, used for sessionStorage key
//   columns     — [{ id, label, pinned?, hint?, align?, width?, render(row) → JSX|string }]
//   defaultVisibleIds — initial toggleable columns (pinned always included)
//   rows        — any[] (shape determined by columns' render fns)
//   emptyLabel  — shown when rows.length === 0
//   actions     — optional JSX, rendered right of the title (sort toggle, etc.)

import React, { useEffect, useState } from 'react';
import { Group, Paper, ScrollArea, Stack, Table, Text, Title } from '@mantine/core';

import { ColumnPicker } from './ColumnPicker';
import { loadColumnPrefs, saveColumnPrefs } from './columnPrefs';

export function ReportTable({
  title,
  subtitle,
  reportId,
  columns,
  defaultVisibleIds,
  rows,
  emptyLabel = 'No data in window.',
  actions,
}) {
  const [visibleIds, setVisibleIds] = useState(() =>
    loadColumnPrefs(reportId, defaultVisibleIds),
  );

  useEffect(() => {
    saveColumnPrefs(reportId, visibleIds);
  }, [reportId, visibleIds]);

  const pinnedColumns = columns.filter((c) => c.pinned);
  const visibleSet = new Set(visibleIds);
  const activeColumns = [
    ...pinnedColumns,
    ...columns.filter((c) => !c.pinned && visibleSet.has(c.id)),
  ];

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="xs" align="flex-start">
        <Stack gap={0}>
          <Title order={5}>{title}</Title>
          {subtitle && <Text size="xs" c="dimmed">{subtitle}</Text>}
        </Stack>
        <Group gap="xs">
          {actions}
          <ColumnPicker columns={columns} visibleIds={visibleIds} onChange={setVisibleIds} />
        </Group>
      </Group>

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">{emptyLabel}</Text>
      ) : (
        <ScrollArea>
          <Table verticalSpacing="xs" highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                {activeColumns.map((c) => (
                  <Table.Th key={c.id} style={{ textAlign: c.align ?? 'left', width: c.width }}>
                    {c.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, i) => (
                <Table.Tr key={row.key ?? row.id ?? i}>
                  {activeColumns.map((c) => (
                    <Table.Td key={c.id} style={{ textAlign: c.align ?? 'left', whiteSpace: c.wrap ? 'normal' : 'nowrap' }}>
                      {c.render(row)}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Paper>
  );
}
